// ═══════════════════════════════════════════════════════════════
// KOTCH - GAME HOOK
// React hook for managing game state
// ═══════════════════════════════════════════════════════════════

import { useReducer, useCallback, useMemo } from 'react';
import type {
  GameState,
  GameActions,
  GameHelpers,
  GameConfig,
  PlayerId,
  Lane,
  LaneCommitment,
  CardDefinition,
  CardInstance,
  Player,
} from '../types/kotch';
import { gameReducer, createInitialState, canConfirmCommitment } from '../utils/kotch/gameLogic';
import { CARD_DEFINITIONS } from '../data/kotch/cards';
import { countCommittedCards, getCommittedLanes } from '../utils/kotch/combatUtils';

// ─────────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────────

export function useKotchGame(): [GameState, GameActions, GameHelpers] {
  const [state, dispatch] = useReducer(gameReducer, null, createInitialState);

  // ─────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────

  const actions: GameActions = useMemo(() => ({
    startGame: (config: GameConfig) => {
      dispatch({ type: 'START_GAME', config });
    },

    resetGame: () => {
      dispatch({ type: 'RESET_GAME' });
    },

    startBattle: () => {
      dispatch({ type: 'START_BATTLE' });
    },

    startRound: () => {
      dispatch({ type: 'START_ROUND' });
    },

    confirmPlayerReady: () => {
      dispatch({ type: 'CONFIRM_PLAYER_READY' });
    },

    selectCard: (cardInstanceId: string | null) => {
      dispatch({ type: 'SELECT_CARD', cardInstanceId });
    },

    hoverLane: (lane: Lane | null) => {
      dispatch({ type: 'HOVER_LANE', lane });
    },

    commitCardToLane: (lane: Lane) => {
      dispatch({ type: 'COMMIT_CARD_TO_LANE', lane });
    },

    uncommitCardFromLane: (lane: Lane) => {
      dispatch({ type: 'UNCOMMIT_CARD_FROM_LANE', lane });
    },

    confirmCommitment: () => {
      dispatch({ type: 'CONFIRM_COMMITMENT' });
    },

    revealCards: () => {
      dispatch({ type: 'REVEAL_CARDS' });
    },

    resolveNextLane: () => {
      dispatch({ type: 'RESOLVE_NEXT_LANE' });
    },

    completeRoundResolution: () => {
      dispatch({ type: 'COMPLETE_ROUND_RESOLUTION' });
    },

    confirmRoundEnd: () => {
      dispatch({ type: 'CONFIRM_ROUND_END' });
    },

    confirmBattleEnd: () => {
      dispatch({ type: 'CONFIRM_BATTLE_END' });
    },

    selectDiscardTarget: (cardInstanceId: string) => {
      dispatch({ type: 'SELECT_DISCARD_TARGET', cardInstanceId });
    },

    aiMakeCommitment: (commitment: LaneCommitment) => {
      dispatch({ type: 'AI_MAKE_COMMITMENT', commitment });
    },

    setMessage: (message: string | null) => {
      dispatch({ type: 'SET_MESSAGE', message });
    },

    setAnimating: (animating: boolean) => {
      dispatch({ type: 'SET_ANIMATING', animating });
    },

    setState: (newState: GameState) => {
      dispatch({ type: 'SET_STATE', state: newState });
    },
  }), []);

  // ─────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────

  const getPlayer = useCallback((id: PlayerId): Player | undefined => {
    return state.players[id];
  }, [state.players]);

  const getCurrentPlayer = useCallback((): Player | undefined => {
    if (state.phase !== 'commitment') return undefined;
    const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
    return state.players[isAttackerTurn ? state.currentAttackerId : state.currentDefenderId];
  }, [state.phase, state.commitmentSubPhase, state.currentAttackerId, state.currentDefenderId, state.players]);

  const getOpponent = useCallback((playerId: PlayerId): Player | undefined => {
    return state.players[playerId === 1 ? 2 : 1];
  }, [state.players]);

  const getCurrentAttacker = useCallback((): Player | undefined => {
    return state.players[state.currentAttackerId];
  }, [state.players, state.currentAttackerId]);

  const getCurrentDefender = useCallback((): Player | undefined => {
    return state.players[state.currentDefenderId];
  }, [state.players, state.currentDefenderId]);

  const isAttacker = useCallback((playerId: PlayerId): boolean => {
    return playerId === state.currentAttackerId;
  }, [state.currentAttackerId]);

  const isDefender = useCallback((playerId: PlayerId): boolean => {
    return playerId === state.currentDefenderId;
  }, [state.currentDefenderId]);

  const getCardDefinition = useCallback((definitionId: string): CardDefinition | undefined => {
    return CARD_DEFINITIONS[definitionId];
  }, []);

  const getCardFromInstance = useCallback((instance: CardInstance): CardDefinition | undefined => {
    return CARD_DEFINITIONS[instance.definitionId];
  }, []);

  const getPlayerCommitment = useCallback((playerId: PlayerId): LaneCommitment => {
    return playerId === 1 ? state.player1Commitment : state.player2Commitment;
  }, [state.player1Commitment, state.player2Commitment]);

  const canCommitToLane = useCallback((playerId: PlayerId, lane: Lane): boolean => {
    const commitment = getPlayerCommitment(playerId);
    return commitment[lane] === null;
  }, [getPlayerCommitment]);

  const getCommittedLanesForPlayer = useCallback((playerId: PlayerId): Lane[] => {
    const commitment = getPlayerCommitment(playerId);
    return getCommittedLanes(commitment);
  }, [getPlayerCommitment]);

  const getCommitmentCount = useCallback((playerId: PlayerId): number => {
    const commitment = getPlayerCommitment(playerId);
    return countCommittedCards(commitment);
  }, [getPlayerCommitment]);

  const mustCommitAtLeast = useCallback((playerId: PlayerId): number => {
    const player = state.players[playerId];
    return player && player.hand.length > 0 ? 1 : 0;
  }, [state.players]);

  const hasCardsInHand = useCallback((playerId: PlayerId): boolean => {
    const player = state.players[playerId];
    return player ? player.hand.length > 0 : false;
  }, [state.players]);

  const isCurrentPlayerAI = useCallback((): boolean => {
    const currentPlayer = getCurrentPlayer();
    return currentPlayer ? currentPlayer.isAI : false;
  }, [getCurrentPlayer]);

  const isPlayerTurn = useCallback((playerId: PlayerId): boolean => {
    if (state.phase !== 'commitment') return false;
    const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
    const expectedPlayer = isAttackerTurn ? state.currentAttackerId : state.currentDefenderId;
    return playerId === expectedPlayer;
  }, [state.phase, state.commitmentSubPhase, state.currentAttackerId, state.currentDefenderId]);

  const getMatchScore = useCallback(() => ({
    p1Battles: state.match.player1BattleWins,
    p2Battles: state.match.player2BattleWins,
  }), [state.match.player1BattleWins, state.match.player2BattleWins]);

  const getBattleScore = useCallback(() => ({
    p1Rounds: state.match.currentBattle.player1RoundWins,
    p2Rounds: state.match.currentBattle.player2RoundWins,
  }), [state.match.currentBattle.player1RoundWins, state.match.currentBattle.player2RoundWins]);

  const canConfirmCommitmentForPlayer = useCallback((playerId: PlayerId): boolean => {
    return canConfirmCommitment(state, playerId);
  }, [state]);

  const helpers: GameHelpers = useMemo(() => ({
    getPlayer,
    getCurrentPlayer,
    getOpponent,
    getCurrentAttacker,
    getCurrentDefender,
    isAttacker,
    isDefender,
    getCardDefinition,
    getCardFromInstance,
    getPlayerCommitment,
    canCommitToLane,
    getCommittedLanes: getCommittedLanesForPlayer,
    getCommitmentCount,
    mustCommitAtLeast,
    hasCardsInHand,
    isCurrentPlayerAI,
    isPlayerTurn,
    getMatchScore,
    getBattleScore,
    canConfirmCommitment: canConfirmCommitmentForPlayer,
  }), [
    getPlayer,
    getCurrentPlayer,
    getOpponent,
    getCurrentAttacker,
    getCurrentDefender,
    isAttacker,
    isDefender,
    getCardDefinition,
    getCardFromInstance,
    getPlayerCommitment,
    canCommitToLane,
    getCommittedLanesForPlayer,
    getCommitmentCount,
    mustCommitAtLeast,
    hasCardsInHand,
    isCurrentPlayerAI,
    isPlayerTurn,
    getMatchScore,
    getBattleScore,
    canConfirmCommitmentForPlayer,
  ]);

  return [state, actions, helpers];
}

export default useKotchGame;
